import { c as _c } from "react/compiler-runtime";
import { diToFormData, type VaultFormData } from '@onefootprint/core';
import type { CustomRequirementSection, DataIdentifier } from '@onefootprint/request-types';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { OTHER_INPUT_SUFFIX } from '../utils/constants';
import { evaluateCondition, getDis } from '../utils/evaluate-condition';

// Address sub-fields that need to be cleared when hiding an address prompt
const ADDRESS_SUB_FIELDS = ['address_line1', 'address_line2', 'city', 'state', 'zip', 'country'];
type ConditionalSectionProps = {
  section: CustomRequirementSection;
  children: ReactNode;
};
export const ConditionalSection = t0 => {
  const $ = _c(5);
  const {
    section,
    children
  } = t0;
  if (!section.condition) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children || $[3] !== section) {
    t1 = <ConditionalVisibility section={section}>{children}</ConditionalVisibility>;
    $[2] = children;
    $[3] = section;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
type ConditionalVisibilityProps = ConditionalSectionProps;
const ConditionalVisibility = ({
  section,
  children
}: ConditionalVisibilityProps) => {
  const form = useFormContext<VaultFormData>();
  // Condition is guaranteed to exist - this component is only rendered when section.condition is truthy
  const condition = section.condition!;
  const [isVisible, setIsVisible] = useState(() => evaluateCondition(condition, form.getValues));
  // When the prompt ends up being hidden, make sure to clear out its value in the frontend.
  // Otherwise, we may accidentally submit stale values for hidden prompts to the backend.
  // To keep the UX snappy, we will save the last entered value in react state so we can restore it
  // if the user toggles the prompt to be visible again.
  const [savedValue, setSavedValue] = useState<unknown>();
  const dependentFields = getDis(condition);
  const updateVisibility = useCallback(debounce(() => {
    setIsVisible(evaluateCondition(condition, form.getValues));
  }, 200), [condition, form.getValues]);
  form.subscribe({
    name: dependentFields,
    formState: {
      values: true
    },
    callback: updateVisibility
  });
  useEffect(() => {
    if (section.kind !== 'prompt') return;
    const fieldNames = getFieldNames(section);
    if (isVisible && savedValue) {
      // Restore all fields
      const saved = savedValue as Record<string, unknown>;
      for (const name of fieldNames) {
        if (name in saved) {
          form.setValue(name, saved[name] as VaultFormData[typeof name]);
        }
      }
    } else if (!isVisible) {
      // Save and clear all fields
      const valuesToSave: Record<string, unknown> = {};
      for (const name_0 of fieldNames) {
        valuesToSave[name_0] = form.getValues(name_0);
        form.setValue(name_0, undefined as never);
      }
      setSavedValue(valuesToSave);
    }
  }, [isVisible]);
  return <AnimatePresence>
      {isVisible && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }}>
          {children}
        </motion.div>}
    </AnimatePresence>;
};

// Helper to get all form field names for a section (handles address, dropdown with "other", etc.)
const getFieldNames = (section: CustomRequirementSection): (keyof VaultFormData)[] => {
  if (section.kind !== 'prompt') return [];
  const baseName = diToFormData(section.di as DataIdentifier);
  if (section.config.kind === 'address') {
    return ADDRESS_SUB_FIELDS.map(field => `${baseName}_${field}` as keyof VaultFormData);
  }
  if (section.config.kind === 'dropdown' && section.config.includeOther) {
    return [baseName as keyof VaultFormData, `${baseName}${OTHER_INPUT_SUFFIX}` as keyof VaultFormData];
  }
  return [baseName as keyof VaultFormData];
};